import { useEffect, useRef } from 'react';
import type { ConfigTemplateFieldSchemaResponse } from '../../../types/configTemplate.types';
import {
    CONFIG_TEMPLATE_COPY,
    changedNothingLine,
    changedRailLabel,
    changedRailNote,
    matchLabel,
    railTotalLine,
} from '../configTemplateCopy';

interface SettingsRailProps {
    schema: ConfigTemplateFieldSchemaResponse;
    included: string[];
    selectedPartId: string | null;
    onSelectPart: (partId: string) => void;
    jumpPartId?: string | null;
    matchCounts?: Record<string, number>;
    groupMatchCounts?: Record<string, number>;
    searchOn?: boolean;
    changed?: {
        count: number;
        sourceName?: string | null;
        selected: boolean;
    } | null;
    onSelectChanged?: () => void;
}

export default function SettingsRail({
    schema,
    included,
    selectedPartId,
    onSelectPart,
    jumpPartId,
    matchCounts,
    groupMatchCounts,
    searchOn = false,
    changed,
    onSelectChanged,
}: SettingsRailProps) {
    const includedSet = new Set(included);
    const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const navRef = useRef<HTMLElement | null>(null);

    const allParts = schema.groups.flatMap((group) => group.parts);
    const allFields = allParts.flatMap((part) => part.clusters.flatMap((c) => c.fields ?? []));
    const totalIn = allFields.filter((field) => includedSet.has(field.path)).length;

    useEffect(() => {
        if (!jumpPartId) return;
        const nav = navRef.current;
        const row = rowRefs.current[jumpPartId];
        if (!nav || !row) return;
        const navRect = nav.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        if (rowRect.top < navRect.top || rowRect.bottom > navRect.bottom) {
            nav.scrollTop += rowRect.top - navRect.top - 8;
        }
    }, [jumpPartId]);

    return (
        <div
            className="flex-none flex flex-col bg-white overflow-hidden"
            style={{
                width: 320,
                border: '1px solid var(--cpm-border-default)',
                borderRadius: 'var(--cpm-radius-xl)',
                minHeight: 0,
                alignSelf: 'stretch',
            }}
        >
            <div
                className="flex items-baseline justify-between"
                style={{
                    gap: 10,
                    padding: '11px 14px',
                    background: 'var(--cpm-purple-tint-3)',
                    borderBottom: '1px solid var(--cpm-border-default)',
                }}
            >
                <span
                    className="uppercase"
                    style={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        color: 'var(--cpm-brand-purple)',
                    }}
                >
                    Pick a part
                </span>
                <span
                    className="tabular-nums"
                    style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cpm-text-secondary)' }}
                >
                    {railTotalLine(totalIn, allFields.length)}
                </span>
            </div>
            <nav
                ref={navRef}
                className="flex flex-col min-h-0 flex-1 overflow-y-auto"
                style={{ padding: 8 }}
            >
                {changed && changed.count === 0 ? (
                    <div
                        style={{
                            padding: '4px 10px 10px',
                            fontSize: 11,
                            fontWeight: 600,
                            lineHeight: 1.45,
                            color: 'var(--cpm-text-tertiary)',
                        }}
                    >
                        {changedNothingLine(changed.sourceName)}
                    </div>
                ) : null}

                {changed && changed.count > 0 ? (
                    <button
                        type="button"
                        onClick={onSelectChanged}
                        className="w-full text-left"
                        style={{
                            border: '1px solid var(--cpm-border-strong)',
                            background: changed.selected
                                ? 'var(--cpm-purple-tint-13)'
                                : 'transparent',
                            borderRadius: 'var(--cpm-radius-10)',
                            padding: '9px 10px',
                            marginBottom: 10,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        <span className="flex items-center w-full" style={{ gap: 8 }}>
                            <span
                                style={{
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                    color: 'var(--cpm-brand-navy)',
                                }}
                            >
                                {CONFIG_TEMPLATE_COPY.CHANGED_SINCE_COPY}
                            </span>
                            <span className="flex-1" />
                            <span
                                className="tabular-nums"
                                style={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: 'var(--cpm-brand-purple)',
                                    background: 'var(--cpm-purple-tint-13)',
                                    borderRadius: 'var(--cpm-radius-full)',
                                    padding: '2px 8px',
                                }}
                            >
                                {changedRailLabel(changed.count)}
                            </span>
                        </span>
                        {changedRailNote(changed.sourceName) ? (
                            <span
                                style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                {changedRailNote(changed.sourceName)}
                            </span>
                        ) : null}
                    </button>
                ) : null}

                {schema.groups.map((group) => {
                    const groupMatches = groupMatchCounts?.[group.id] ?? 0;
                    return (
                        <div key={group.id} style={{ marginBottom: 8 }}>
                            <div
                                className="flex items-baseline"
                                style={{ gap: 8, padding: '6px 8px 4px' }}
                            >
                                <span
                                    style={{
                                        fontSize: 12.5,
                                        fontWeight: 800,
                                        letterSpacing: '-0.01em',
                                        color: 'var(--cpm-text-primary)',
                                    }}
                                >
                                    {group.title}
                                </span>
                                <span className="flex-1" />
                                {searchOn && groupMatches > 0 ? (
                                    <span
                                        style={{
                                            fontSize: 10.5,
                                            fontWeight: 800,
                                            color: 'var(--cpm-brand-purple)',
                                        }}
                                    >
                                        {matchLabel(groupMatches)}
                                    </span>
                                ) : null}
                                <span
                                    className="tabular-nums"
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    {(() => {
                                        const fields = group.parts.flatMap((part) =>
                                            part.clusters.flatMap((cluster) => cluster.fields ?? [])
                                        );
                                        const inCount = fields.filter((field) =>
                                            includedSet.has(field.path)
                                        ).length;
                                        return `${inCount} / ${fields.length}`;
                                    })()}
                                </span>
                            </div>
                            {group.parts.map((part) => {
                                const fields = part.clusters.flatMap(
                                    (cluster) => cluster.fields ?? []
                                );
                                const inCount = fields.filter((field) =>
                                    includedSet.has(field.path)
                                ).length;
                                const active = !changed?.selected && selectedPartId === part.id;
                                const partMatches = matchCounts?.[part.id] ?? 0;
                                return (
                                    <button
                                        key={part.id}
                                        ref={(node) => {
                                            rowRefs.current[part.id] = node;
                                        }}
                                        type="button"
                                        onClick={() => onSelectPart(part.id)}
                                        className="flex items-center w-full text-left"
                                        style={{
                                            gap: 8,
                                            padding: '8px 10px',
                                            border: 'none',
                                            borderRadius: 9,
                                            background: active
                                                ? 'var(--cpm-purple-tint-13)'
                                                : 'transparent',
                                            cursor: 'pointer',
                                        }}
                                        onMouseEnter={(event) => {
                                            if (!active) {
                                                event.currentTarget.style.background =
                                                    'var(--cpm-purple-tint-3)';
                                            }
                                        }}
                                        onMouseLeave={(event) => {
                                            event.currentTarget.style.background = active
                                                ? 'var(--cpm-purple-tint-13)'
                                                : 'transparent';
                                        }}
                                    >
                                        <span
                                            className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                                            style={{
                                                fontSize: 12.5,
                                                fontWeight: active ? 800 : 600,
                                                color: active
                                                    ? 'var(--cpm-brand-navy)'
                                                    : 'var(--cpm-text-secondary)',
                                            }}
                                        >
                                            {part.title}
                                        </span>
                                        <span className="flex-1" />
                                        {searchOn && partMatches > 0 ? (
                                            <span
                                                className="flex-none"
                                                style={{
                                                    fontSize: 10.5,
                                                    fontWeight: 800,
                                                    color: 'var(--cpm-brand-purple)',
                                                }}
                                            >
                                                {matchLabel(partMatches)}
                                            </span>
                                        ) : null}
                                        <span
                                            className="flex-none tabular-nums"
                                            style={{
                                                fontSize: 11.5,
                                                fontWeight: 800,
                                                color: 'var(--cpm-brand-purple)',
                                            }}
                                        >
                                            {inCount}/{fields.length}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    );
                })}
            </nav>
        </div>
    );
}
